/**
 * useActionTiming — measure a user-initiated action (save, publish, delete) the
 * way the user experiences it: click → the screen is done moving.
 *
 * Page timing answers "how slow was this screen to load". It says nothing about
 * the thing people actually complain about, which is pressing Save and waiting.
 * A save that the server answers in 150ms can still cost seconds on the client:
 * the mutation's `invalidateQueries` refetches the list and the detail, and the
 * page re-renders every row. `PageTimingProvider` cannot see any of that — it
 * emits once per navigation and is long done by then.
 *
 * So each action is recorded as its own sample, split into the two halves that
 * point at different fixes:
 *
 *   requestMs — call → the mutation promise resolves (network + server + the
 *               mutation's own onSuccess). Slow here ⇒ look at the backend.
 *   settleMs  — that moment → every query the action invalidated has refetched
 *               AND the resulting render has painted. Slow here ⇒ look at the
 *               client (refetch fan-out, unmemoized lists).
 *
 * Rides the same pipe as page perf: WebLogger → /console/client-logs →
 * page-perf container, so it lands in admin → Monitoring → Page Perf with no
 * new endpoint. See `clientLogs.ts` for the ingestion side.
 *
 * Usage — wrap the awaited action at the call site, not inside `mutationFn`
 * (mutationFn resolves before the invalidation, which is the half we care about):
 *
 *   const timeAction = useActionTiming();
 *   onSave={(data) => timeAction('saveQuestion', () =>
 *     updateMutation.mutateAsync({ id, data }).then(onDone))}
 *
 * Design: docs/FeatureDesigns/page-load-perf-drilldown.md
 */

import { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { WebLogger, WebComponentCode, getCurrentPageLoadId } from '@shared-ui/logging';
// Direct path, not the package index — that barrel also pulls in the action
// tracker and its router dependency, which this hook has no use for.
import { normalizePageLabel } from '../user-actions/pageLabel';
import { usePageTimingReport } from './pageTimingContext';

const logger = new WebLogger(WebComponentCode.WEB_PERF, 'ActionPerf');

/** How often to re-check react-query for idle while waiting for an action to settle. */
const POLL_MS = 25;

/**
 * Stop waiting after this long and record the sample as `capped`. An action
 * still in flight past a minute is a hang, not a slow save, and a page with a
 * polling query on it would otherwise never report at all. Capped samples are
 * counted separately in the admin view so the truncation is never silent.
 */
const SETTLE_CAP_MS = 60_000;

const nextTick = () => new Promise<void>(resolve => setTimeout(resolve, 0));

/** Resolve after the browser has painted, so the settle time includes the
 *  re-render the fresh data caused — usually the expensive half. */
const afterPaint = () => new Promise<void>(resolve => {
    if (typeof requestAnimationFrame !== 'function') { setTimeout(resolve, 0); return; }
    requestAnimationFrame(() => setTimeout(resolve, 0));
});

/**
 * Wait until nothing is fetching, then until the next paint.
 *
 * `invalidateQueries` refetches active queries synchronously, so one tick is
 * enough for the count to have gone up before we start watching it come down.
 */
async function waitForSettle(qc: QueryClient): Promise<{ ms: number; capped: boolean }> {
    const start = performance.now();
    await nextTick();
    let capped = false;
    while (qc.isFetching() > 0) {
        if (performance.now() - start >= SETTLE_CAP_MS) { capped = true; break; }
        await new Promise<void>(resolve => setTimeout(resolve, POLL_MS));
    }
    if (!capped) await afterPaint();
    return { ms: Math.round(performance.now() - start), capped };
}

export type ActionTimer = <T>(action: string, run: () => Promise<T>) => Promise<T>;

/**
 * Returns a wrapper that times an async action and emits one perf sample.
 *
 * The wrapper is transparent: it returns whatever `run` returns and rethrows
 * whatever it throws, so wrapping an existing call site changes no behaviour.
 * A failed action is still recorded (with `ok: false`) — errors have their own
 * timing profile and hiding them would bias the percentiles.
 */
export function useActionTiming(): ActionTimer {
    const qc = useQueryClient();
    const { page: providerPage } = usePageTimingReport();

    return useCallback(async <T,>(action: string, run: () => Promise<T>): Promise<T> => {
        // Prefer the enclosing provider's label (each app normalizes its own
        // routes); fall back to the shared normalizer so an app without a
        // PageTimingProvider still reports something aggregatable.
        const page = providerPage
            || (typeof window !== 'undefined' ? normalizePageLabel(window.location.pathname) : 'unknown');
        const start = performance.now();
        let ok = true;
        try {
            return await run();
        } catch (err) {
            ok = false;
            throw err;
        } finally {
            const requestMs = Math.round(performance.now() - start);
            // Never let measurement break the action it is measuring.
            void (async () => {
                try {
                    const { ms: settleMs, capped } = await waitForSettle(qc);
                    logger.info2(`${page} ${action} action perf`, {
                        kind: 'action',
                        page,
                        action,
                        pageLoadId: getCurrentPageLoadId(),
                        totalMs: requestMs + settleMs,
                        requestMs,
                        settleMs,
                        ok,
                        capped,
                    });
                } catch { /* perf telemetry is best-effort */ }
            })();
        }
    }, [qc, providerPage]);
}
